import { useState, useEffect, useRef, useCallback } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const App = () => {
  // Existing chat state
  const [value, setValue] = useState("");
  const [prevChats, setPrevChats] = useState([]);
  const [currentTitle, setCurrentTitle] = useState(null);
  const feedRef = useRef(null);

  // Pod / streaming state
  const [isLoading, setIsLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayFading, setOverlayFading] = useState(false);
  const [statusMessages, setStatusMessages] = useState([]);
  const [pullProgress, setPullProgress] = useState(null);
  const [streamedContent, setStreamedContent] = useState("");
  const [infoPanelData, setInfoPanelData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [podState, setPodState] = useState("IDLE");
  const [chatError, setChatError] = useState(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Mobile info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Mask state
  const [showWelcomeMask, setShowWelcomeMask] = useState(true);
  const [showShutdownMask, setShowShutdownMask] = useState(false);
  const [maskFading, setMaskFading] = useState(false);
  const hasBeenReady = useRef(false);

  // Password gate state
  const [showPasswordGate, setShowPasswordGate] = useState(
    () => sessionStorage.getItem("chatgpu_auth") !== "granted"
  );
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const currentChat = prevChats.filter(
    (prevChat) => prevChat.title === currentTitle
  );
  const uniqueTitles = Array.from(
    new Set(prevChats.map((previousChat) => previousChat.title))
  );

  // --- Auto-scroll ---
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [prevChats, streamedContent]);

  // --- Track when pod has been ready (for shutdown mask) ---
  useEffect(() => {
    if (podState === "READY") {
      hasBeenReady.current = true;
    }
    if (podState === "IDLE" && hasBeenReady.current) {
      setShowShutdownMask(true);
      setMaskFading(false);
    }
  }, [podState]);

  // --- Pod status polling for countdown ---
  useEffect(() => {
    if (podState !== "READY" && podState !== "PROCESSING") return;

    const poll = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/pod-status`);
        const data = await resp.json();
        setPodState(data.state);
        setTimeRemaining(data.timeRemaining);
        if (data.state === "IDLE") {
          setTimeRemaining(null);
        }
      } catch {
        // Ignore polling errors
      }
    };

    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [podState]);

  // --- Local countdown tick ---
  const hasTimeRemaining = timeRemaining !== null && timeRemaining > 0;
  useEffect(() => {
    if (!hasTimeRemaining) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null || prev <= 1000) {
          setPodState("IDLE");
          return null;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [hasTimeRemaining]);

  // --- Password gate ---
  const handlePasswordSubmit = () => {
    const correct = process.env.REACT_APP_ACCESS_PASSWORD || "Projects2026!";
    if (passwordInput === correct) {
      sessionStorage.setItem("chatgpu_auth", "granted");
      setShowPasswordGate(false);
    } else {
      setPasswordError(true);
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === "Enter") handlePasswordSubmit();
    if (passwordError) setPasswordError(false);
  };

  // --- Create new chat ---
  const createNewChat = () => {
    setValue("");
    setCurrentTitle(null);
    setStreamedContent("");
  };

  // --- Switch chat ---
  const handleClick = (uniqueTitle) => {
    setCurrentTitle(uniqueTitle);
    setStreamedContent("");
    setSidebarOpen(false);
  };

  // --- Toggle sidebar ---
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // --- Process SSE events from a ReadableStream ---
  // Shared by both the warmup flow and the chat flow.
  // Returns when the stream ends. Calls onReady when READY is received.
  async function processSSEStream(response, { onReady, onToken, onDone, onError }) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let overlayDismissed = false;

    while (true) {
      const { done, value: chunk } = await reader.read();
      if (done) break;

      buffer += decoder.decode(chunk, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();

      for (const part of parts) {
        const line = part.trim();
        if (!line || !line.startsWith("data: ")) continue;

        let event;
        try {
          event = JSON.parse(line.slice(6));
        } catch {
          continue;
        }

        switch (event.type) {
          case "status": {
            if (
              event.stage !== "READY" ||
              event.coldStartDuration > 0
            ) {
              if (event.stage !== "READY") {
                setShowOverlay(true);
                setOverlayFading(false);
              }
            }
            setPodState(event.stage === "READY" ? "READY" : event.stage);

            const isProgress = event.stage === "STARTING_PROGRESS";
            setStatusMessages((prev) => {
              if (isProgress && prev.length > 0 && prev[prev.length - 1].isProgress) {
                return [
                  ...prev.slice(0, -1),
                  { text: event.message, isProgress: true },
                ];
              }
              return [
                ...prev,
                {
                  text: event.message,
                  isSuccess: event.stage === "READY",
                  isProgress,
                },
              ];
            });

            if (event.stage === "READY" && !overlayDismissed) {
              overlayDismissed = true;
              setPullProgress(null);
              setTimeout(() => {
                setOverlayFading(true);
                setTimeout(() => {
                  setShowOverlay(false);
                  setOverlayFading(false);
                }, 400);
              }, 1500);
              if (onReady) onReady(event);
            }
            break;
          }

          case "pull_progress": {
            if (event.total && event.completed && event.total > 0) {
              setPullProgress({
                status: event.status,
                total: event.total,
                completed: event.completed,
              });
            } else if (event.status === "success") {
              setPullProgress(null);
            }
            break;
          }

          case "token": {
            if (!overlayDismissed) {
              overlayDismissed = true;
              setOverlayFading(true);
              setTimeout(() => {
                setShowOverlay(false);
                setOverlayFading(false);
              }, 400);
            }
            if (onToken) onToken(event);
            break;
          }

          case "done": {
            if (onDone) onDone(event);
            break;
          }

          case "error": {
            setStatusMessages((prev) => [
              ...prev,
              { text: event.message, isError: true },
            ]);
            // Keep overlay open so user sees error + retry button
            if (onError) onError(event);
            break;
          }

          default:
            break;
        }
      }
    }
  }

  // --- Start warmup (shared by welcome mask + shutdown mask) ---
  const startWarmup = useCallback(async () => {
    setStatusMessages([]);
    setPullProgress(null);
    setChatError(null);
    setShowOverlay(true);
    setOverlayFading(false);

    try {
      const response = await fetch(`${API_URL}/api/pod/warmup`);
      if (!response.ok) {
        throw new Error(`Warmup failed: ${response.status}`);
      }

      await processSSEStream(response, {
        onReady: () => {
          setTimeRemaining(300000);
        },
        onError: (event) => {
          setChatError({
            message: event.message,
            errorCode: event.errorCode || "UNKNOWN",
          });
        },
      });
    } catch (err) {
      console.error("Warmup error:", err);
      setChatError({ message: err.message, errorCode: "CONNECTION" });
      setShowOverlay(false);
    }
  }, []);

  // --- Welcome mask: start pod ---
  const handleWelcomeStart = () => {
    setMaskFading(true);
    setTimeout(() => {
      setShowWelcomeMask(false);
      setMaskFading(false);
      startWarmup();
    }, 400);
  };

  // --- Shutdown mask: restart pod ---
  const handleShutdownRestart = () => {
    setMaskFading(true);
    setTimeout(() => {
      setShowShutdownMask(false);
      setMaskFading(false);
      startWarmup();
    }, 400);
  };

  // --- Send message via SSE ---
  const getMessages = useCallback(async () => {
    if (!value || value.trim() === "" || isLoading) return;

    const currentMessage = value.trim();
    setValue("");

    const title = currentTitle || currentMessage;
    if (!currentTitle) {
      setCurrentTitle(currentMessage);
    }

    setPrevChats((prev) => [
      ...prev,
      { title, role: "user", content: currentMessage },
    ]);

    setIsLoading(true);
    setStreamedContent("");
    setStatusMessages([]);
    setPullProgress(null);
    setChatError(null);

    const history = currentChat.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    const accRef = { current: "" };

    try {
      const submitResp = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: currentMessage, history }),
      });

      if (!submitResp.ok) {
        throw new Error(`Failed to submit message: ${submitResp.status}`);
      }

      const { requestId } = await submitResp.json();

      const response = await fetch(
        `${API_URL}/api/chat/stream/${requestId}`
      );

      if (!response.ok) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      await processSSEStream(response, {
        onReady: () => {
          setTimeRemaining(300000);
        },
        onToken: (event) => {
          accRef.current += event.content;
          setStreamedContent(accRef.current);
        },
        onDone: (event) => {
          setPrevChats((prev) => [
            ...prev,
            { title, role: "assistant", content: accRef.current },
          ]);
          setStreamedContent("");
          setInfoPanelData({
            gpuType: event.gpuType,
            model: event.model,
            coldStart: event.timings.coldStart,
            inference: event.timings.inference,
            totalTokens: event.timings.totalTokens,
            tokensPerSecond: event.timings.tokensPerSecond,
            cost: event.cost.totalCost,
            hourlyRate: event.cost.hourlyRate,
          });
          setPodState("READY");
          setTimeRemaining(300000);
        },
        onError: (event) => {
          setChatError({
            message: event.message,
            errorCode: event.errorCode || "UNKNOWN",
          });
        },
      });
    } catch (err) {
      console.error("Chat error:", err);
      const msg = `Connection error: ${err.message}`;
      setStatusMessages((prev) => [...prev, { text: msg, isError: true }]);
      setChatError({ message: msg, errorCode: "CONNECTION" });
      setShowOverlay(false);
    } finally {
      setIsLoading(false);
    }
  }, [value, isLoading, currentTitle, currentChat]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      getMessages();
    }
  };

  // --- Error helpers ---
  function errorTitle(code) {
    switch (code) {
      case "INSUFFICIENT_FUNDS":
        return "Insufficient RunPod Funds";
      case "NO_GPU_AVAILABLE":
        return "No GPUs Available";
      case "GPU_TOO_EXPENSIVE":
        return "GPUs Over Budget";
      case "CONNECTION":
        return "Connection Error";
      default:
        return "Error";
    }
  }

  function errorHint(code) {
    switch (code) {
      case "INSUFFICIENT_FUNDS":
        return "The RunPod account linked to this app does not have enough credits to start a GPU pod. The site owner needs to add funds at runpod.io/console/user/billing.";
      case "NO_GPU_AVAILABLE":
        return "None of the configured GPU types are currently available on RunPod's community cloud. Inventory changes frequently — please try again in a few minutes.";
      case "GPU_TOO_EXPENSIVE":
        return "All available GPUs that can run this model exceed the $0.60/hr cost limit configured for this app. This can happen when cheaper GPUs are temporarily out of stock.";
      case "CONNECTION":
        return "Could not reach the backend server. Check your network connection or try refreshing the page.";
      default:
        return "Something went wrong. Please try again.";
    }
  }

  // --- Helpers ---
  function formatTime(ms) {
    if (ms === null || ms === undefined) return "--:--";
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, "0")}`;
  }

  function formatBytes(bytes) {
    if (!bytes) return "0 B";
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(1)}GB`;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)}MB`;
  }

  function formatMs(ms) {
    if (!ms || ms === 0) return "Warm";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  // --- Progress bar display text ---
  function progressBarText() {
    if (!pullProgress) return "";
    const pct = Math.round(
      (pullProgress.completed / pullProgress.total) * 100
    );
    if (pct >= 100) {
      return "Download complete! Finalizing...";
    }
    return `${pct}% (${formatBytes(pullProgress.completed)} / ${formatBytes(
      pullProgress.total
    )})`;
  }

  // --- Render ---
  return (
    <div className="app">
      {/* Password Gate */}
      {showPasswordGate && (
        <div className="password-gate">
          <div className="password-gate-box">
            <p className="screen-mask-title">ChatGPU</p>
            <p className="password-gate-text">
              This is a live demo of a project that runs a real AI
              model on on-demand cloud GPUs. To prevent unauthorized API usage
              and unexpected charges, access is password-protected.
              <br />
              <br />
              To request access, reach out at{" "}
              <a
                href="mailto:juliusholbert@gmail.com"
                className="password-gate-link"
              >
                juliusholbert@gmail.com
              </a>
              .
            </p>
            <div className="password-gate-input-row">
              <input
                className="password-gate-input"
                type="password"
                placeholder="Enter password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  if (passwordError) setPasswordError(false);
                }}
                onKeyDown={handlePasswordKeyDown}
                autoFocus
              />
              <button
                className="screen-mask-btn password-gate-btn"
                onClick={handlePasswordSubmit}
              >
                Enter
              </button>
            </div>
            {passwordError && (
              <p className="password-gate-error">Incorrect password. Please try again.</p>
            )}
          </div>
        </div>
      )}

      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Hamburger Toggle Button - hidden when overlays/masks are shown */}
      {!showWelcomeMask && !showShutdownMask && !showOverlay && (
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle menu">
          {sidebarOpen ? "\u2715" : "\u2630"}
        </button>
      )}

      <section className={`side-bar ${sidebarOpen ? "open" : ""}`}>
        <button onClick={() => { createNewChat(); setSidebarOpen(false); }}>+ New Chat</button>
        <ul className="history">
          {uniqueTitles?.map((uniqueTitle, index) => (
            <li key={index} onClick={() => handleClick(uniqueTitle)}>
              {uniqueTitle}
            </li>
          ))}
        </ul>
        <nav>
          <p>Made by Julius</p>
        </nav>
      </section>

      <section className="main" style={{ position: "relative" }}>
        {!currentTitle && <h1>ChatGPU</h1>}

        <ul className="feed" ref={feedRef}>
          {currentChat?.map((chatMessage, index) => (
            <li key={index}>
              <p className="role">{chatMessage.role}</p>
              <p>{chatMessage.content}</p>
            </li>
          ))}
          {streamedContent && (
            <li>
              <p className="role">assistant</p>
              <p className="streaming-cursor">{streamedContent}</p>
            </li>
          )}
        </ul>

        {chatError && (
          <div className="error-banner">
            <div className="error-banner-header">
              <span className="error-banner-icon">!</span>
              <strong>{errorTitle(chatError.errorCode)}</strong>
              <button
                className="error-banner-dismiss"
                onClick={() => setChatError(null)}
                aria-label="Dismiss"
              >
                x
              </button>
            </div>
            <p className="error-banner-hint">
              {errorHint(chatError.errorCode)}
            </p>
            {chatError.errorCode !== "CONNECTION" && (
              <p className="error-banner-detail">{chatError.message}</p>
            )}
          </div>
        )}

        <div className="bottom-section">
          {/* Mobile-only Technical Info button */}
          {infoPanelData && (
            <button
              className="mobile-info-btn"
              onClick={() => setShowInfoModal(true)}
            >
              Technical Info
            </button>
          )}
          <div className="input-container">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder={
                isLoading ? "Waiting for response..." : "Send a message"
              }
            />
            <div
              id="submit"
              onClick={getMessages}
              style={{ opacity: isLoading ? 0.4 : 1 }}
            >
              &#10146;
            </div>
          </div>
          <p className="info">
            Running Llama 3.1 8B on RunPod GPU. The GPU pod automatically shuts
            down after 5 minutes of inactivity to save costs. Cold starts may
            require 30–90 seconds.
          </p>
        </div>

        {/* Cold Start Overlay */}
        {showOverlay && (
          <div
            className={`cold-start-overlay ${overlayFading ? "fade-out" : ""}`}
          >
            <div className="cold-start-terminal">
              <div className="terminal-header">
                <div className="terminal-spinner"></div>
                <span className="terminal-header-text">
                  Starting GPU Pod...
                </span>
              </div>

              {statusMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`terminal-line ${
                    msg.isSuccess
                      ? "success"
                      : msg.isError
                      ? "error-line"
                      : ""
                  }`}
                >
                  {msg.text}
                </div>
              ))}

              {pullProgress && pullProgress.total > 0 && (
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${Math.round(
                        (pullProgress.completed / pullProgress.total) * 100
                      )}%`,
                    }}
                  ></div>
                  <div className="progress-bar-text">
                    {progressBarText()}
                  </div>
                </div>
              )}

              {/* Retry section after error */}
              {statusMessages.some((msg) => msg.isError) && (
                <div className="terminal-retry-section">
                  <p className="terminal-retry-prompt">
                    Would you like to try again?
                  </p>
                  <button
                    className="terminal-retry-btn"
                    onClick={() => {
                      setStatusMessages([]);
                      setPullProgress(null);
                      setChatError(null);
                      startWarmup();
                    }}
                  >
                    Retry
                  </button>
                  <p className="terminal-retry-disclaimer">
                    This system uses on-demand GPU provisioning, which
                    introduces several external dependencies outside of
                    application control. Failures can occur during GPU
                    scheduling, container image retrieval, runtime
                    initialization, or model loading. These issues are typically
                    caused by transient infrastructure constraints such as GPU
                    availability, network congestion, registry throttling, or
                    regional latency. All failures occur before inference begins
                    and do not affect user data.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Countdown Timer */}
      {timeRemaining !== null &&
        podState !== "IDLE" &&
        !showShutdownMask && (
          <div
            className={`countdown-timer ${
              timeRemaining < 15000
                ? "critical"
                : timeRemaining < 60000
                ? "warning"
                : ""
            }`}
          >
            <span className="countdown-label">Auto-shutdown</span>
            {formatTime(timeRemaining)}
          </div>
        )}

      {/* Info Panel (desktop only) */}
      {infoPanelData && !showShutdownMask && (
        <div className="info-panel desktop-only">
          <div className="info-panel-title">Request Stats</div>
          <div className="info-panel-row">
            <span className="info-panel-label">GPU</span>
            <span className="info-panel-value">
              {infoPanelData.gpuType?.replace("NVIDIA GeForce ", "").replace("NVIDIA ", "") || "\u2014"}
            </span>
          </div>
          <div className="info-panel-row">
            <span className="info-panel-label">Model</span>
            <span className="info-panel-value">
              {infoPanelData.model || "\u2014"}
            </span>
          </div>
          <div className="info-panel-row">
            <span className="info-panel-label">Cold Start</span>
            <span className="info-panel-value">
              {formatMs(infoPanelData.coldStart)}
            </span>
          </div>
          <div className="info-panel-row">
            <span className="info-panel-label">Inference</span>
            <span className="info-panel-value">
              {formatMs(infoPanelData.inference)}
            </span>
          </div>
          <div className="info-panel-row">
            <span className="info-panel-label">Tokens/sec</span>
            <span className="info-panel-value">
              {infoPanelData.tokensPerSecond || "\u2014"}
            </span>
          </div>
          <div className="info-panel-row">
            <span className="info-panel-label">Cost</span>
            <span className="info-panel-value">
              ${infoPanelData.cost?.toFixed(4) || "0.0000"}
            </span>
          </div>
        </div>
      )}

      {/* Info Modal (mobile only) */}
      {showInfoModal && infoPanelData && (
        <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <span className="info-modal-title">Technical Info</span>
              <button
                className="info-modal-close"
                onClick={() => setShowInfoModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="info-modal-content">
              <div className="info-panel-row">
                <span className="info-panel-label">GPU</span>
                <span className="info-panel-value">
                  {infoPanelData.gpuType?.replace("NVIDIA GeForce ", "").replace("NVIDIA ", "") || "\u2014"}
                </span>
              </div>
              <div className="info-panel-row">
                <span className="info-panel-label">Model</span>
                <span className="info-panel-value">
                  {infoPanelData.model || "\u2014"}
                </span>
              </div>
              <div className="info-panel-row">
                <span className="info-panel-label">Cold Start</span>
                <span className="info-panel-value">
                  {formatMs(infoPanelData.coldStart)}
                </span>
              </div>
              <div className="info-panel-row">
                <span className="info-panel-label">Inference</span>
                <span className="info-panel-value">
                  {formatMs(infoPanelData.inference)}
                </span>
              </div>
              <div className="info-panel-row">
                <span className="info-panel-label">Tokens/sec</span>
                <span className="info-panel-value">
                  {infoPanelData.tokensPerSecond || "\u2014"}
                </span>
              </div>
              <div className="info-panel-row">
                <span className="info-panel-label">Cost</span>
                <span className="info-panel-value">
                  ${infoPanelData.cost?.toFixed(4) || "0.0000"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Mask */}
      {showWelcomeMask && (
        <div className={`screen-mask ${maskFading ? "fade-out" : ""}`}>
          <div className="screen-mask-box">
            <p className="screen-mask-title">ChatGPU</p>
            <p className="screen-mask-text">
              This app runs Llama 3.1 8B on a cloud GPU powered by RunPod.
              Starting the service takes a couple of minutes to create a GPU
              pod, download the LLM, and install the required software in the
              cloud.
              <br />
              <br />
              GPU availability depends on RunPod inventory and current pricing.
              If no GPUs are available, you may need to try again in a few
              minutes.
            </p>
            <button className="screen-mask-btn" onClick={handleWelcomeStart}>
              Start GPU Pod
            </button>
          </div>
        </div>
      )}

      {/* Shutdown Mask */}
      {showShutdownMask && (
        <div className={`screen-mask ${maskFading ? "fade-out" : ""}`}>
          <div className="screen-mask-box">
            <p className="screen-mask-title">Pod Shut Down</p>
            <p className="screen-mask-text">
              The GPU pod was automatically shut down due to inactivity. Your
              chat history is still here. Click below to spin up a new pod and
              continue chatting.
            </p>
            <button
              className="screen-mask-btn"
              onClick={handleShutdownRestart}
            >
              Spin Up Another Pod
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
